import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Cap } from '../../primitives'
import { CAPABILITIES, Capability, CAPABILITY_ORDER } from './capabilities'

interface Idea {
  title: string
  /** Why Cai is raising it. An idea that cannot say this is just a notification. */
  because: string
  /** New, Recommended, Beta — the slot is held open whether or not one is set. */
  tag?: string
}

/**
 * Every one is work Bluey can take on, named by the verb it would perform —
 * never an observation with a button bolted to it. What it noticed belongs in
 * `because`, which is the case for doing the thing, not the thing itself.
 */
const IDEAS: Record<Capability, Idea[]> = {
  [Capability.INSURANCE]: [
    {
      title: 'Add your four newest trucks to the policy',
      tag: 'New',
      because:
        'They have been running since September 12 and are not on it. Anything that happens on a load is uninsured until they are, and the carrier backdates the premium either way, so waiting saves nothing.',
    },
    {
      title: 'Request fresh loss runs from your carriers',
      because:
        'The current set expires in 11 days, and every carrier you quote with asks for them before pricing anything. Two of yours take a week to send them, so starting now is what keeps the renewal on time.',
    },
    {
      title: 'Raise your cargo limit to $250k',
      because:
        'Three recent reefer loads were worth more than the $100k you carry, so a total loss on any of them comes out of the business rather than the policy. The extra limit costs less than one of those loads.',
    },
  ],
  [Capability.BOOKKEEPING]: [
    {
      title: 'Categorise two September repairs',
      because:
        'Tambrey flagged them at close and they are still open, so September is not finally closed and the quarter cannot be filed from it. Both are under $900, and one looks like it belongs to the trailer.',
    },
    {
      title: 'Split DEF out of your fuel charges',
      because:
        'Both post to one account, which hides what diesel actually costs you per mile and makes the fuel line look worse than it is. Splitting them is what lets cost per mile mean anything at all.',
    },
  ],
  [Capability.COMPLIANCE]: [
    {
      title: 'Correct your truck count with FMCSA',
      because:
        'CAB still shows 5. You told me 9. That count drives your safety scoring and the premium every carrier quotes off, so being wrong by four works against you in both places until it is corrected.',
    },
    {
      title: 'Collect updated medical cards from two drivers',
      because:
        'Pete Frazier expires in 27 days, with one more behind him. A driver whose card has lapsed cannot legally run, and getting an appointment inside a month is the part that usually slips.',
    },
    {
      title: 'Prepare your Q4 IFTA return',
      tag: 'Recommended',
      because:
        'Filing opens in three weeks and your mileage is already in, so this is reconciliation rather than collection. Late returns carry a penalty in every jurisdiction, which is where it gets expensive.',
    },
  ],
  [Capability.CFO]: [
    {
      title: 'Build a plan to get cost per mile to $1.85',
      because:
        'You are running $1.94 against the target you set. Nine cents across the miles this fleet turns is about $4,800 a month, and most of the gap sits in fuel and in two lanes that stopped paying.',
    },
    {
      title: 'Model adding three more trucks',
      tag: 'Beta',
      because:
        'What it does to margin, before you sign for them. Three more means drivers, insurance and payments landing before the first settlement does, and the answer depends which lanes you put them on.',
    },
  ],
  [Capability.FUEL]: [
    {
      title: 'Reroute your I-75 fuel stops',
      because:
        'Diesel is 22¢ cheaper eight miles off the lane your drivers use. Across the loads you run through there each month that is roughly $600, and the detour adds twenty minutes to a ten hour day.',
    },
    {
      title: 'Set idle alerts on three trucks',
      because:
        'All three are running over 40% idle on the Cincinnati run, which burns close to a gallon an hour for nothing and shortens the service interval. An alert in the cab is what actually changes it.',
    },
  ],
}

/**
 * The case for buying a capability, made where its work is listed rather than
 * on a button bolted to the heading: the point is not that the product exists,
 * it is that the agent could be doing these for you.
 */
const ADS: Partial<Record<Capability, { title: string; body: string }>> = {
  [Capability.INSURANCE]: {
    title: 'Insurance with Cai',
    body: 'Buy your policy through Cai and the agent can chase claims, issue certificates and handle the renewal.',
  },
  [Capability.BOOKKEEPING]: {
    title: 'Bookkeeping with Cai',
    body: 'Connect your books and the agent can categorise, reconcile and chase what is missing.',
  },
}

function AdCard({ capability }: { capability: Capability }) {
  const ad = ADS[capability]
  if (!ad) return null

  return (
    <div className="border-brand-200 bg-brand/5 flex h-30 items-center gap-3 rounded-xl border p-3">
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <span className="text-ink line-clamp-2 text-xs leading-snug font-medium">{ad.title}</span>
        <span className="text-ink-500 line-clamp-3 text-[10px] leading-snug">{ad.body}</span>
      </div>
      <Button size="xs" className="shrink-0">
        {CAPABILITIES[capability].cta}
      </Button>
    </div>
  )
}

function IdeaCard({ idea, gated }: { idea: Idea; gated?: boolean }) {
  return (
    <div
      className={cn(
        'border-ink-100 flex h-30 items-center gap-3 rounded-xl border bg-white p-3',
        // Nothing here can be acted on until the books are connected, so the
        // card shows the work without offering to start it.
        gated && 'opacity-60'
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        {idea.tag && (
          <Badge variant="secondary" className="self-start px-1.5 py-0 text-[10px]">
            {idea.tag}
          </Badge>
        )}
        <span className="text-ink line-clamp-2 text-xs leading-snug font-medium">{idea.title}</span>
        <span className="text-ink-400 line-clamp-3 text-[10px] leading-snug">{idea.because}</span>
      </div>
      {!gated && (
        <Button size="xs" variant="outline" className="shrink-0">
          Send to Cai
        </Button>
      )}
    </div>
  )
}

export function IdeasTab({ contentWidth }: { contentWidth?: number }) {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div
        className="mx-auto flex w-full flex-col gap-5 p-3"
        style={contentWidth ? { maxWidth: contentWidth } : undefined}
      >
        <h2 className="text-ink text-sm font-bold tracking-tight">Ideas</h2>
        {/* What the fleet already has comes first; the offers gather at the bottom
          rather than interrupting the work in hand. */}
        {CAPABILITY_ORDER.filter((entry) => entry !== Capability.FUEL)
          .sort((a, b) => Number(CAPABILITIES[b].enabled) - Number(CAPABILITIES[a].enabled))
          .map((entry) => {
            const capability = CAPABILITIES[entry]
            return (
              <div key={entry} className="flex flex-col gap-2">
                <Cap>{capability.short}</Cap>
                {!capability.enabled && <AdCard capability={entry} />}
                {IDEAS[entry].map((idea) => (
                  <IdeaCard key={idea.title} idea={idea} gated={entry === Capability.BOOKKEEPING} />
                ))}
              </div>
            )
          })}
      </div>
    </div>
  )
}
